import type {
  AddonsValidation,
  AddonsWrapper,
  ViewColumn,
  ViewColumnsWrapper,
  ViewConfig,
  ViewConfigsWrapper,
  ViewConfiguration,
} from '../../@types/views';
import { isOfflineEnabled } from '../../db/odin-db-util';
import { getSortEntityType } from '../../hooks/use-grid-columns-settings';
import { transformFormAddons, transformFormViewConfigrations } from '../../utils/data-transformation-utils';
import { resolveLastEditedFilterConfigValue, resolveRecordFilterConfigValue } from '../../utils/dynamic-page-utils';
import { buildGlobalHierarchyFiltersUrl } from '../../utils/filter-url-builder-utils';
import type { RootState } from '../store';
import { providesList } from '../util/api-util';
import { api } from './api';
import { recordApi } from './record';

export const viewApi = api
  .enhanceEndpoints({ addTagTypes: ['ViewConfig', 'AllFormsViewConfig', 'ViewColumn'] })
  .injectEndpoints({
    overrideExisting: 'throw',
    endpoints: (builder) => ({
      getViewConfigs: builder.query<Array<ViewConfig>, { formId?: number }>({
        query: (args: { formId: number }) => `/forms/${args.formId}/user-view-configurations`,
        transformResponse: (response: ViewConfigsWrapper) => transformFormViewConfigrations(response),
        providesTags: (result) => providesList(result, 'ViewConfig'),
        onQueryStarted: async (args, { dispatch, queryFulfilled, getState }) => {
          if (!isOfflineEnabled) {
            return;
          }

          const { data } = await queryFulfilled;
          const defaultViewConfig = data.find((config) => config.defaultConfig);
          if (defaultViewConfig) {
            // trigger the prefetch of related metadata for default view config only
            const viewColumnsDispatchResult = dispatch(
              viewApi.endpoints.getViewColumns.initiate({ configId: defaultViewConfig.id })
            );

            // attempts to recreate the record list and record list count request structure for default view to ensure cache hits
            try {
              // initial sort directive (needs more work to handle all cases)
              const columns = await viewColumnsDispatchResult.unwrap();

              const initialSort = columns
                .filter((column) => column.sortColumn)
                .map((column) => ({
                  id: column.id,
                  sortOrder: column.ascending ? '' : '-',
                  entityId: column.entityReference.entityId,
                  sortEntityType: getSortEntityType(column.entityReference.entityType),
                }));

              let sortDirective = '';
              for (let i = 0, length = initialSort.length; i < length; i++) {
                const { sortOrder, entityId, sortEntityType } = initialSort[i];
                sortDirective += `&sort_directive=${sortOrder}${sortEntityType}:${entityId}`;
              }

              // initial filters
              const recordsFilter = resolveRecordFilterConfigValue(defaultViewConfig?.archivedType);
              const lastEditedFilter = resolveLastEditedFilterConfigValue(defaultViewConfig?.dateRange);
              const filters = {
                archivedStep: {
                  value: recordsFilter.value,
                  comparison: '=',
                  customDataProperties: undefined,
                },
                lastModifiedDateRange: {
                  value: lastEditedFilter.value,
                  comparison: '=',
                  customDataProperties: undefined,
                },
              };

              // global hierarchy filters
              const state = getState() as RootState;
              const globalFilters = state.globalHierarchyFilters.selectedHierarchies;
              const globalFiltersMatchingOption = state.globalHierarchyFilters.matchingOption;
              const globalHierarchyFilters = buildGlobalHierarchyFiltersUrl(globalFilters, globalFiltersMatchingOption);

              // record list data
              dispatch(
                recordApi.util.prefetch(
                  'getRecords',
                  // args need to match component api call to ensure cache hits
                  {
                    keyTypeId: true,
                    page: 1,
                    pageSize: 50,
                    formId: args.formId!,
                    filters,
                    sortDirective,
                    globalHierarchyFilters,
                  },
                  { force: true }
                )
              );

              // record list count data
              dispatch(
                recordApi.util.prefetch(
                  'getRecordCount',
                  {
                    formId: args.formId!,
                    filters,
                    globalHierarchyFilters,
                  },
                  { force: true }
                )
              );
            } finally {
              viewColumnsDispatchResult.unsubscribe();
            }
          }
        },
      }),
      getViewColumns: builder.query<Array<ViewColumn>, { configId?: number }>({
        query: (args: { configId: number }) => `/user-view-configurations/${args.configId}`,
        providesTags: (result, error, { configId }) => [{ type: 'ViewColumn', id: configId }],
        transformResponse: (response: ViewColumnsWrapper) =>
          response.result?.columnConfigurations?.filter((viewColumn) => viewColumn.draftMarker !== true) ?? [],
      }),
      updateViewConfig: builder.mutation<void, Pick<ViewConfiguration, 'id'> & Partial<ViewConfiguration>>({
        query: ({ id, skipTagInValidation, ...patch }) => ({
          url: `/user-view-configurations/${id}`,
          method: 'PUT',
          body: patch,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        invalidatesTags: (result, error, { id, skipTagInValidation }) => {
          if (skipTagInValidation) {
            return [];
          }

          return [
            { type: 'ViewConfig', id: id },
            { type: 'AllFormsViewConfig', id: id },
            { type: 'ViewColumn', id: id },
          ];
        },
      }),
      deleteViewConfig: builder.mutation<void, number>({
        query: (id) => ({
          url: `/user-view-configurations/${id}`,
          method: 'DELETE',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        invalidatesTags: [
          { type: 'ViewConfig', id: 'LIST' },
          { type: 'AllFormsViewConfig', id: 'LIST' },
        ],
      }),
      addViewConfig: builder.mutation<void, Partial<ViewConfiguration>>({
        query: (arg) => ({
          url: `/user-view-configurations`,
          method: 'POST',
          body: arg,
        }),
        invalidatesTags: [
          { type: 'ViewConfig', id: 'LIST' },
          { type: 'AllFormsViewConfig', id: 'LIST' },
        ],
      }),
      getAllFormsUserViewConfigurations: builder.query<Array<ViewConfig>, number>({
        query: (moduleId: number) => `/modules/${moduleId}/all-forms/user-view-configurations`,
        transformResponse: (response: ViewConfigsWrapper) => transformFormViewConfigrations(response),
        providesTags: (result) => providesList(result, 'AllFormsViewConfig'),
      }),
      getFormAddOns: builder.query<Array<AddonsValidation>, number>({
        query: (formId: number) => `/forms/${formId}/add-ons`,
        transformResponse: (response: AddonsWrapper) => transformFormAddons(response?.result) ?? [],
      }),
      getDefaultView: builder.query<Array<ViewColumn>, { formId?: number }>({
        query: (args: { formId: number }) => `/forms/${args.formId}/default-view`,
        transformResponse: (response: ViewColumnsWrapper) =>
          response.result?.columnConfigurations?.filter((viewColumn) => viewColumn.draftMarker !== true) ?? [],
      }),
      getAllFormsDefaultView: builder.query<Array<ViewColumn>, { moduleId?: number }>({
        query: (args: { moduleId: number }) => `/modules/${args.moduleId}/all-forms/default-view`,
        transformResponse: (response: ViewColumnsWrapper) =>
          response.result?.columnConfigurations?.filter((viewColumn) => viewColumn.draftMarker !== true) ?? [],
      }),
    }),
  });

export const {
  useGetViewConfigsQuery,
  useGetViewColumnsQuery,
  useUpdateViewConfigMutation,
  useDeleteViewConfigMutation,
  useAddViewConfigMutation,
  useGetAllFormsUserViewConfigurationsQuery,
  useGetFormAddOnsQuery,
  useGetDefaultViewQuery,
  useGetAllFormsDefaultViewQuery,
} = viewApi;
